import React from 'react';
import { ResponsiveContainer, CartesianGrid, ComposedChart, XAxis, YAxis, Bar, ReferenceLine, Tooltip } from 'recharts';
import { styled, useTheme } from '@mui/material';
import { returnThemeSpacingAsNumber } from '../../../../utils/functions';
import { AxisDomain, CartesianGridType, ChartData } from '../../ChartsTypes';
import TooltipBody from '../TooltipBody';
import { getSeries, getTicks } from '../../series';
import { colors } from '../../../../utils/palette';
import { VerticalAxisTick } from '../AxisTick/Vertical';

const ResponsiveContainerWrapper = styled(ResponsiveContainer)(() => ({
  justifyContent: 'flex-start',
}));

// Props per il BarChart
export interface BarChartProps {
  type: 'barChart';
  data: ChartData[];
  height?: number;
  palette?: string[];
  barSize?: number;
  tooltipFooter?: 'sum' | 'max';
  cartesianGrid?: CartesianGridType;
  yTicks?: AxisDomain;
  tooltipValueFormatter?: (v: number) => string | number;
  tooltipFooterValueFormatter?: (v: number) => string;
  stackedBars?: boolean;
  referenceLineValue?: number;
  applyDefaultSort?: boolean;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  height = 500,
  palette,
  barSize,
  tooltipFooter,
  cartesianGrid,
  yTicks,
  applyDefaultSort = true,
  tooltipValueFormatter,
  tooltipFooterValueFormatter,
  stackedBars,
  referenceLineValue,
}) => {
  const theme = useTheme();

  const ticks = yTicks ? getTicks(yTicks) : [];
  const xAxisKey = '__xAxis__';

  // Transforms the data to include the xAxisLabel as a key in the data object
  const chartData = data?.map(({ values, xAxisLabel }) => ({
    ...values,
    [xAxisKey]: xAxisLabel,
  }));

  const graphSeries = getSeries(chartData, palette);

  return (
    <ResponsiveContainerWrapper width="100%" height={height}>
      <ComposedChart margin={{ bottom: returnThemeSpacingAsNumber(theme.spacing(1.5)) }} data={chartData}>
        <XAxis axisLine={false} tickLine={false} style={{ fontSize: '10px' }} dataKey={xAxisKey} />
        <YAxis
          axisLine={false}
          tickLine={false}
          ticks={ticks.length ? ticks : undefined}
          domain={yTicks ? [yTicks.min, yTicks.max] : undefined}
          tick={<VerticalAxisTick />}
        />
        {cartesianGrid && <CartesianGrid vertical={cartesianGrid.verticalLines} horizontal={cartesianGrid.horizontalLines} stroke={colors.lightSecondary} />}

        <Tooltip
          wrapperStyle={{
            outline: 'none',

            color: colors.white,
          }}
          cursor={false}
          content={
            <TooltipBody
              valueFormatter={tooltipValueFormatter ? tooltipValueFormatter : (n) => n}
              series={graphSeries}
              footerValueFormatter={tooltipFooterValueFormatter}
              footer={tooltipFooter}
              applyDefaultSort={applyDefaultSort}
              sx={{ top: -55 }}
            />
          }
        />

        {graphSeries.map(({ name, color, opacity, stackId }) => (
          <Bar key={name} dataKey={name} barSize={barSize} fill={color} fillOpacity={opacity ?? 1} stackId={stackedBars ? stackId : undefined} />
        ))}
        {referenceLineValue && <ReferenceLine y={referenceLineValue} stroke="black" />}
      </ComposedChart>
    </ResponsiveContainerWrapper>
  );
};

export default BarChart;
